import React, { useState, useEffect, useRef } from "react";
import BasicInfoContainer from "./BasicInfoContainer";
import OperationsContainer from "../operation/OperationsContainer";
import BackgroundInfoContainer from "./BackgroundInfoContainer";
import CommitmentReportContainer from "./CommitmentReportContainer";
import AgendaContainer from "../agenda/AgendaContainer";
import LanguageSelection from "../../materialCommitment/commitment/LanguageSelection";
import TitleDisplay from "../../materialCommitment/commitment/TitleDisplay";

import { bindActionCreators } from "redux";
import { useDispatch, useSelector } from "react-redux";

import {
  showSuccessNotification,
  startedSavingNotification,
  showSaveDraftNotification,
  showDeletedNotification,
  showErrorNotification,
  showErrorTextNotification,
  showSuccessTextNotification
} from "../../materialCommitment/helpers/Notifications";

import commitmentSlice from "../../../state/slices/commitmentSlice";
import validatorSlice from "../../../state/slices/validatorSlice";
import moment from "moment";
import { getCommitmentTypeId } from "../../materialCommitment/helpers/Categories";
import translate from "../../reusable/translate";

// Commitment creation form for only Circular Economy Green Deals
const CEGDCommitmentCreationForm = ({
  fetchedCommitment,
  isJoining,
  commitmentToJoin,
  commitmentCategoryId,
  organizationName,
  createdByUserId,
  mainGoal,
  circularEconomyOperations
}) => {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const { validators, commitment } = useSelector(state => state);

  const {
    changeTitleValidator,
    changeCityValidator,
    changeShortDescValidator,
    changeOperationsValidator,
    changeMeterValidator,
    changeLevelValidator,
    changeOwnMeterValidator
  } = bindActionCreators(validatorSlice.actions, dispatch); //validators

  const {
    changeCategoryId,
    changeCommitmentId,
    changeCreatedByUserId,
    changeCommitmentType,
    changeOrganizationName,
    changeStatus,
    setCategoryIds,
    setCommitment,
    resetCommitment
  } = bindActionCreators(commitmentSlice.actions, dispatch); //commitment fields

  useEffect(() => {
    if (isJoining && commitmentToJoin) {
      fetchedCommitment = {
        ...fetchedCommitment,
        commitment: commitmentToJoin
      };
      fetchedCommitment.commitment = {
        ...fetchedCommitment.commitment,
        id: "",
        commitmentData: {
          commitmentType: commitmentToJoin.commitmentType,
          categoryIds: commitmentToJoin.categoryIds
        }
      };
    }
    changeCategoryId(
      commitmentCategoryId ||
        (fetchedCommitment.commitment &&
        fetchedCommitment.commitment.commitmentCategoryId
          ? fetchedCommitment.commitment.commitmentCategoryId
          : "")
    );
    changeCommitmentId(
      (fetchedCommitment.commitment && fetchedCommitment.commitment.id) || ""
    );
    changeCreatedByUserId(
      createdByUserId || fetchedCommitment.commitment.createdByUserId
    );
    changeCommitmentType(fetchedCommitment.commitmentData.commitmentType || "");
    changeOrganizationName(
      organizationName || fetchedCommitment.commitment.organizationName
    );
    changeStatus(
      (fetchedCommitment.commitment && fetchedCommitment.commitment.status) ||
        ""
    );
    setCategoryIds(
      fetchedCommitment.commitment
        ? [fetchedCommitment.commitment.categoryIds]
        : []
    );

    setLoading(false);
  }, []);

  // useRefs for scrolling when invalid form
  const titleRef = useRef();
  const shortDescriptionRef = useRef();
  const backgroundDescriptionRef = useRef();
  const operationsRef = useRef();
  const meterRef = useRef();
  const dateRef = useRef();

  useEffect(() => {
    setCommitment({
      ...fetchedCommitment.commitment,
      categoryIds: commitment.categoryIds.concat(
        fetchedCommitment.commitmentData.categoryIds
      ),
      reportingInterval: "CUSTOM"
    });

    return () => {
      // called when component unmounts
      resetCommitment();
    };
  }, []);

  useEffect(() => {
    if (!commitment.createdByUserId && createdByUserId) {
      changeCreatedByUserId(createdByUserId);
    }
  }, [commitment.createdByUserId]);

  const saveDraft = () => {
    saveCommitment(
      {
        ...commitment,
        categoryIds: commitment.categoryIds.filter(Number),
        organizationName:
          commitment.organizationName === "Yksityishenkilönä"
            ? ""
            : commitment.organizationName,
        status: "draft"
      },
      "draft"
    );
  };

  const redirectToCommitments = () => {
    window.setTimeout(() => {
      window.location.href = "/profiili#/sitoumukset";
    }, 1500);
  };

  const saveApproved = () => {
    const isCeilingCommitment = commitment.operations.some(
      op => op.operationTheme === "X.1."
    );
    //Must have at least 2 operations (or 3 if 'kattositoumus' operation is selected)
    const requiredOps = isCeilingCommitment ? 3 : 2;

    // form validation and saving
    if (!commitment.title_fi_FI) {
      changeTitleValidator(false);
      titleRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    } else if (!commitment.address) {
      changeCityValidator(false);
      titleRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      return;
    } else if (!commitment.shortDescription_fi_FI) {
      changeShortDescValidator(false);
      shortDescriptionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      return;
    } else if (commitment.operations.length < requiredOps) {
      changeOperationsValidator(false);
      operationsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      return;
      //MainGoal validator is always true, Adjust logic from agendacontainer...
    } else if (!validators.mainGoal) {
      showErrorTextNotification(
        "Valitse sitoumukselle vähintään yksi muutosalue"
      );
      return;
    }

    let hasStartAndTargetLevel = true;
    let hasOwnMeter = true;
    let hasMeters = true;
    let isTitleMissing = false;
    const themes = new Set();
    commitment.operations.forEach(o => {
      themes.add(o.operationTheme);
      if (!isTitleMissing) {
        isTitleMissing = o.operationTitle_fi_FI === "";
      }
      if (o.meters.length === 0) {
        hasMeters = false;
      } else {
        o.meters.forEach(m => {
          hasOwnMeter = m.meterType_fi_FI !== "";
          hasStartAndTargetLevel =
            (m.startingLevel !== "" && m.targetLevel !== "") ||
            !(m.meterValueType === "NUMBER");
        });
      }
    });

    // Must be at least two operations from different themes
    if (themes.size < requiredOps || isTitleMissing) {
      changeOperationsValidator(false);
      operationsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      if (themes.size < requiredOps) {
        showErrorTextNotification(
          `Toimenpiteiden tulee olla vähintään kahdelta eri toimenpidealueelta.
            ${isCeilingCommitment ? "Kattositoumusta ei lasketa." : ""}`
        );
      }
      return;
    }
    if (!hasMeters) {
      changeMeterValidator(false);
      meterRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      showErrorTextNotification(
        "Täytäthän toimenpiteiltä puuttuvat mittariparit."
      );
      return;
    }
    if (!hasStartAndTargetLevel) {
      changeLevelValidator(false);
      meterRef.current.scrollIntoView({
        behavior: "smooth"
      });
      return;
    }
    if (!hasOwnMeter) {
      changeOwnMeterValidator(false);
      meterRef.current.scrollIntoView({
        behavior: "smooth"
      });
      return;
    }

    saveCommitment(
      {
        ...commitment,
        categoryIds: commitment.categoryIds.filter(Number),
        organizationName:
          commitment.organizationName === "Yksityishenkilönä"
            ? ""
            : commitment.organizationName,
        status: commitment.status,
        startDate: commitment.startDate || moment(new Date()).toJSON(),
        operations: commitment.operations.map(mappedOperation => ({
          ...mappedOperation,
          published: true
        }))
      },
      "approved"
    ).then(() => redirectToCommitments());
  };

  const saveCommitment = async (commitmentToSave, status) => {
    if (saving) return;

    setSaving(true);
    startedSavingNotification();
    await fetch("/o/commitment2050-service/commitmentapi/commitments", {
      method: "POST",
      body: JSON.stringify(commitmentToSave),
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
        "Cache-Control": "no-cache"
      },
      credentials: "include"
    }).then(res => {
      if (res.status < 300) {
        if (status === "draft") {
          showSaveDraftNotification();
        } else if (status === "approved") {
          showSuccessNotification();
        }
      } else if (res.status > 300) {
        showErrorNotification();
      }

      res.json().then(res => {
        changeCommitmentId(res.id);

        if (isJoining) {
          const formData = new FormData();
          formData.append("parentCommitmentId", commitmentToJoin.id);
          formData.append("childCommitmentId", res.id);
          fetch(
            "/o/commitment2050-service/commitmentapi/commitments/jointoparent",
            {
              method: "POST",
              body: formData,
              headers: {
                Accept: "application/json, text/plain, */*",
                "Content-Type": "multipart/form-data",
                "Cache-Control": "no-cache"
              },
              credentials: "include"
            }
          ).then(res2 => {
            if (res2.status < 300) {
              showSuccessTextNotification(
                translate({ textKey: "sit.notification.success.joining" })
              );
            } else if (res2.status > 300) {
              showErrorTextNotification(
                translate({ textKey: "sit.notification.error.joining" })
              );
            }
          });
        }

        setSaving(false);
        if (status === "approved") {
          fetch("/o/commitment2050-service/commitmentapi/sendemailnotify", {
            method: "POST",
            body: JSON.stringify(res),
            headers: {
              Accept: "application/json, text/plain, */*",
              "Content-Type": "application/json",
              "Cache-Control": "no-cache"
            },
            credentials: "include"
          }).then();
        }
      });
    });
  };

  const deleteDraft = async () => {
    try {
      await fetch(
        `/o/commitment2050-service/commitmentapi/commitments/drafts/${commitment.id}`,
        {
          method: "DELETE"
        }
      ).then(res => {
        if (res.status === 200) {
          //STATUS OK
          showDeletedNotification();
          redirectToCommitments();
        }
      });
    } catch (err) {
      console.error("ERR", err);
    }
  };

  const printRef = useRef();

  if (loading) return null;
  return (
    <div ref={printRef}>
      <TitleDisplay
        saveDraft={saveDraft}
        text={"Kiertotalouden green deal -sitoumuksen luominen"}
        categoryId={getCommitmentTypeId(commitment.commitmentType)}
        status={commitment.status}
      />

      <LanguageSelection hideSv />

      <BasicInfoContainer // Contains info about creator and commitment
        shortDescriptionRef={shortDescriptionRef}
        dateRef={dateRef}
        titleRef={titleRef}
        fetchedCommitment={fetchedCommitment}
      />

      <div className="page-break-after" />

      <OperationsContainer // Contains operations & meters
        availableOperations={circularEconomyOperations}
        meterRef={meterRef}
        operationsRef={operationsRef}
      />

      <div className="page-break-after" />

      <BackgroundInfoContainer // Contains mainly text boxes
        backgroundDescriptionRef={backgroundDescriptionRef}
      />

      <div className="page-break-after" />

      <AgendaContainer // Contains goals and agenda goals
        fetchedCommitment={fetchedCommitment}
        mainGoal={mainGoal}
      />

      <CommitmentReportContainer // Contains checkboxes and save, delete etc buttons
        saveDraft={saveDraft}
        saveApproved={saveApproved}
        deleteDraft={deleteDraft}
        printRef={printRef}
      />
    </div>
  );
};

export default CEGDCommitmentCreationForm;



// WEBPACK FOOTER //
// ./src/components/circularEconomyCommitment/commitment/CEGDCommitmentCreationForm.jsx